import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ImageIcon, X, Upload, Video, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { useToast } from "@/hooks/use-toast";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

interface MediaUploadProps {
  onMediaUploaded?: (urls: string[]) => void;
  maxVideoSizeMB?: number;
  acceptedFormats?: string;
}

export const MediaUpload = ({ 
  onMediaUploaded,
  maxVideoSizeMB = 100,
  acceptedFormats = "image/*,video/*"
}: MediaUploadProps) => {
  const { orgId } = useOrgPermissions();
  const [uploading, setUploading] = useState(false);
  const [mediaUrls, setMediaUrls] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const { toast } = useToast();

  const MAX_VIDEO_SIZE_BYTES = maxVideoSizeMB * 1024 * 1024; // Convert MB to bytes
  
  const isVideo = (filename: string) => {
    return /\.(mp4|mov|avi|webm|mkv)$/i.test(filename);
  };

  const getFileType = (filename: string): 'image' | 'video' => {
    return isVideo(filename) ? 'video' : 'image';
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Validate file sizes first
    const oversizedFiles = Array.from(files).filter(file => {
      if (isVideo(file.name) && file.size > MAX_VIDEO_SIZE_BYTES) {
        return true;
      }
      return false;
    });

    if (oversizedFiles.length > 0) {
      toast({
        title: "File too large",
        description: `Video files must be under ${maxVideoSizeMB}MB. Found: ${oversizedFiles.map(f => `${f.name} (${formatFileSize(f.size)})`).join(', ')}`,
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const totalFiles = files.length;
      let completed = 0;

      const uploadPromises = Array.from(files).map(async (file) => {
        const fileExt = file.name.split('.').pop();
        const timestamp = Date.now();
        const random = Math.random().toString(36).substring(7);
        const prefix = orgId ? `${orgId}/${user.id}` : user.id;
        const fileName = `${prefix}/${timestamp}-${random}.${fileExt}`;

        // For videos, use chunked upload (simulated with progress)
        const { error: uploadError, data } = await supabase.storage
          .from('post-media')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        completed++;
        setUploadProgress(Math.round((completed / totalFiles) * 100));

        const { data: { publicUrl } } = supabase.storage
          .from('post-media')
          .getPublicUrl(fileName);

        return publicUrl;
      });

      const urls = await Promise.all(uploadPromises);
      const newMediaUrls = [...mediaUrls, ...urls];
      setMediaUrls(newMediaUrls);

      if (onMediaUploaded) {
        onMediaUploaded(newMediaUrls);
      }

      const videoCount = Array.from(files).filter(f => isVideo(f.name)).length;
      const imageCount = files.length - videoCount;
      
      toast({
        title: "Media uploaded",
        description: `${imageCount > 0 ? `${imageCount} image(s)` : ''}${imageCount > 0 && videoCount > 0 ? ' and ' : ''}${videoCount > 0 ? `${videoCount} video(s)` : ''} uploaded successfully`,
      });
    } catch (error) {
      console.error("Upload error:", error);
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Failed to upload media. Please try again.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Reset the input
      event.target.value = '';
    }
  };

  const handleRemove = (index: number) => {
    const newMediaUrls = mediaUrls.filter((_, i) => i !== index);
    setMediaUrls(newMediaUrls);
    if (onMediaUploaded) {
      onMediaUploaded(newMediaUrls);
    }
  };

  return (
    <div className="space-y-4">
      <input
        type="file"
        accept={acceptedFormats}
        multiple
        onChange={handleFileSelect}
        className="hidden"
        id="media-upload"
        disabled={uploading}
      />

      {mediaUrls.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {mediaUrls.map((url, index) => {
            const isVideoFile = /\.(mp4|mov|avi|webm|mkv)$/i.test(url);
            
            return (
              <div key={index} className="relative group">
                <div className="w-full h-24 rounded-lg overflow-hidden bg-muted">
                  {isVideoFile ? (
                    <div className="relative w-full h-full">
                      <video
                        src={url}
                        className="w-full h-full object-cover"
                        muted
                        playsInline
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <Video className="w-6 h-6 text-white" />
                      </div>
                      <Badge 
                        variant="secondary" 
                        className="absolute bottom-1 left-1 text-xs"
                      >
                        Video
                      </Badge>
                    </div>
                  ) : (
                    <img
                      src={url}
                      alt={`Upload ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <Button
                  size="icon"
                  variant="destructive"
                  className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => handleRemove(index)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {uploading && uploadProgress > 0 && (
        <div className="space-y-2">
          <Progress value={uploadProgress} className="h-2" />
          <p className="text-justify text-xs text-center text-muted-foreground">
            Uploading... {uploadProgress}%
          </p>
        </div>
      )}

      <label
        htmlFor="media-upload"
        className={`border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-base cursor-pointer block ${
          uploading ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        {uploading ? (
          <>
            <Loader2 className="w-8 h-8 mx-auto mb-2 text-muted-foreground animate-spin" />
            <p className="text-justify text-sm text-muted-foreground">Uploading media...</p>
            <p className="text-justify text-xs text-muted-foreground mt-1">Please wait</p>
          </>
        ) : (
          <>
            <div className="flex items-center justify-center gap-2 mb-2">
              <ImageIcon className="w-8 h-8 text-muted-foreground" />
              <Video className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-justify text-sm text-muted-foreground">Click to upload media</p>
            <p className="text-justify text-xs text-muted-foreground mt-1">
              Images (JPG, PNG, GIF) or Videos (MP4, MOV, WebM)
            </p>
            <p className="text-justify text-xs text-muted-foreground mt-1">
              Max video size: {maxVideoSizeMB}MB
            </p>
          </>
        )}
      </label>
    </div>
  );
};
